import mixin from "@/mixin/index.js";
import { lockPortrait } from '@/platform/device.js';
let sqlite_db = null;
let sqliteDbReady = null;
const clinicalDraftQueues = new Map();
const clinicalDraftUsersBeingCleared = new Set();

const fixed_urls = {
    foods: 'https://firebasestorage.googleapis.com/v0/b/vetalfa-prd.appspot.com/o/summary%2Ffoods_database.json?alt=media&token=a7a1c633-f21e-4ea3-8791-8f6abe4a2271',
    oral_hygiene: 'https://firebasestorage.googleapis.com/v0/b/vetalfa-prd.appspot.com/o/summary%2Foral_hygiene_database.json?alt=media&token=d58f2a0e-504a-48ad-86bc-b30c24b15e0a',
    supplements: 'https://firebasestorage.googleapis.com/v0/b/vetalfa-prd.appspot.com/o/summary%2Fsupplements_database.json?alt=media&token=1bfad02c-ea6f-414f-adb6-30965dbcae77'
}

document.addEventListener('deviceready', function () {
    sqlite_db = window.sqlitePlugin.openDatabase(
        {
            name: 'db_vetalfa',
            location: 'default',
            androidDatabaseProvider: 'system'
        },
        opened_db => {
            sqliteDbReady = new Promise((resolve, reject) => {
                opened_db.transaction(
                    tx => {
                        console.log("Creating tables....")
                        tx.executeSql('CREATE TABLE IF NOT EXISTS configuration (id, value)');
                        tx.executeSql('CREATE UNIQUE INDEX IF NOT EXISTS idx_configuration ON configuration (id)');
                        tx.executeSql('CREATE TABLE IF NOT EXISTS drugs (id, value)');
                        tx.executeSql('CREATE UNIQUE INDEX IF NOT EXISTS idx_drugs ON drugs (id)');
                        tx.executeSql('CREATE TABLE IF NOT EXISTS products (id, value)');
                        tx.executeSql('CREATE UNIQUE INDEX IF NOT EXISTS idx_products ON products (id)');
                    },
                    e => { console.log(`Error creating database: ${e.message}`); reject(e); },
                    () => { console.log(`Database created`); resolve(sqlite_db); });
            });
        }
    );
    lockPortrait();
});

const getUserFromStorage = (successCb, errorCb) => {
    if (mixin.methods._isCordovaApp()) {
        if (!sqlite_db || !sqlite_db.dbname || sqlite_db.dbname != "db_vetalfa") {
            return setTimeout(_ => { getUserFromStorage(successCb, errorCb) }, 100);
        } else {
            sqlite_db.executeSql('SELECT value AS value FROM configuration WHERE id = ?', ["user"], function (rs) {
                if (rs.rows.length) {
                    return successCb(mixin.methods._decrypt(rs.rows.item(0).value));
                } else {
                    return errorCb("User not found");
                }
            }, function (error) {
                return errorCb("User not found:" + error.message);
            });
        }
    } else {
        let user = window.localStorage.getItem("vetalfa");
        if (user) {
            try {
                user = JSON.parse(window.atob(user));
                window.localStorage.removeItem("vetalfa");
                window.localStorage.setItem("vetalfa", mixin.methods._encrypt(user));
                return successCb(user);
            } catch {
                return successCb(mixin.methods._decrypt(user));
            }
        }
        return errorCb("User not found");
    }
}

const readFromStaticDb = async function (entity, id) {
    let fixed_database = null;
    try {
        if (entity === 'foods') {
            fixed_database = await import(/* webpackChunkName: "vetalfa_fd_foods" */ '../../public/foods_database.json');
        } else if (entity === 'oral_hygiene') {
            fixed_database = await import(/* webpackChunkName: "vetalfa_fd_oral_hygiene" */ '../../public/oral_hygiene_database.json');
        } else if (entity === 'supplements') {
            fixed_database = await import(/* webpackChunkName: "vetalfa_fd_supplements" */ '../../public/supplements_database.json');
        }
    } catch (err) {
        let response = await fetch(fixed_urls[entity]);
        response = await response.json();
        response = JSON.stringify(response, null, 2);
        fixed_database = JSON.parse(response);
    }

    if (!fixed_database) {
        fixed_database = await import(/* webpackChunkName: "vetalfa_fd" */ '../../public/database.json');
    }
    let result = fixed_database[entity];
    if (id) {
        result = result.find(e => {
            return mixin.methods._createIdentifier(e.id) === mixin.methods._createIdentifier(id);
        }).value;
    }
    if (result) {
        try {
            return JSON.parse(mixin.methods._decrypt(result));
        } catch {
            return mixin.methods._decrypt(result);
        }
    }
}

const selectAsync = async (entity, id) => {
    return new Promise((resolve, reject) => {
        let table = id ? entity : "configuration";
        sqlite_db.executeSql(`SELECT value AS value FROM ${table} WHERE id = ?`, [id || entity], (rs) => {
            resolve(rs);
        }, reject);
    });
}

const storageSelect = async (entity, id) => {
    if (['foods', 'oral_hygiene', 'supplements', 'synonyms', 'global_summary'].includes(entity)) {
        return await readFromStaticDb(entity, id);
    } else if (mixin.methods._isCordovaApp()) {
        try {
            if (entity !== "version" && !entity.includes("user") && entity !== 'configuration') {
                throw new Error('Using fixed DB.');
            }
            let rs = await selectAsync(entity, id);
            if (rs.rows.length) {
                return mixin.methods._decrypt(rs.rows.item(0).value);
            } else {
                return await readFromStaticDb(entity, id);
            }
        } catch {
            return await readFromStaticDb(entity, id);
        }
    } else if (entity == "user") {
        let user = window.localStorage.getItem("vetalfa");
        if (user) return mixin.methods._decrypt(user);
    } else {
        let document = window.localStorage.getItem("vetalfa_" + id);
        if (!!document) {
            return mixin.methods._decrypt(document);
        }
        throw new Error(`Entity not found: ${id}`);
    }
}

const storageDeleteWithId = (entity, id, successCb, errorCb) => {
    if (mixin.methods._isCordovaApp()) {
        let table = id ? entity : "configuration";
        id = id || entity;
        return sqlite_db.executeSql(`DELETE FROM ${table} WHERE id = ?`, [id], successCb,
            error => { errorCb("Error deleting entity:" + error.message); }
        );
    } else if (entity == "user") {
        window.localStorage.removeItem("vetalfa");
        return successCb();
    }
    return errorCb(`Entity not found:${id ? `${entity}_${id}` : entity}`);
}

const storageUpsert = (entity, id, document, successCb = () => { }, errorCb = () => { }) => {
    if (mixin.methods._isCordovaApp()) {
        let table = id ? entity : "configuration";
        id = id || entity;
        return sqlite_db.executeSql(`INSERT OR REPLACE INTO ${table} (id, value) VALUES (?1,?2)`, [id, mixin.methods._encrypt(document)],
            successCb,
            error => { errorCb("Error upserting entity:" + error.message); }
        );
    } else if (entity == "user") {
        window.localStorage.removeItem("vetalfa");
        window.localStorage.setItem("vetalfa", mixin.methods._encrypt(document));
    } else {
        const table = "vetalfa_" + id;
        window.localStorage.removeItem(table);
        window.localStorage.setItem(table, mixin.methods._encrypt(document));
    }
    return successCb();
}

const waitForClinicalDraftDb = async () => {
    const deadline = Date.now() + 5000;
    while (!sqliteDbReady) {
        if (Date.now() >= deadline) throw new Error('Local storage unavailable');
        await new Promise(resolve => setTimeout(resolve, 100));
    }
    return await sqliteDbReady;
};

const queueClinicalDraftOperation = (key, operation) => {
    const previous = clinicalDraftQueues.get(key) || Promise.resolve();
    const queued = previous.catch(() => undefined).then(operation);
    clinicalDraftQueues.set(key, queued);
    const cleanup = () => {
        if (clinicalDraftQueues.get(key) === queued) clinicalDraftQueues.delete(key);
    };
    queued.then(cleanup, cleanup);
    return queued;
};

const parseClinicalDraftKey = key => {
    if (typeof key !== 'string') return null;
    const parts = key.split(':');
    // Four-part keys are accepted only so logout can remove drafts written by
    // 5.17.0 before organization scoping was introduced. They are never read.
    if (parts[0] !== 'clinical_draft' || ![4, 5].includes(parts.length) || parts.slice(1).some(part => !part)) return null;
    return { userId: parts[1], legacy: parts.length === 4 };
};

// Drafts use the configuration table on Cordova and encrypted localStorage on web.
// No clinical payload is logged; writes/removals are serialized per context.
const clinicalDraftStorage = async (operation, key, value) => {
    const parsedKey = parseClinicalDraftKey(key);
    if (!parsedKey || parsedKey.legacy) throw new Error('Invalid draft context');
    if (!['read', 'write', 'delete'].includes(operation)) throw new Error('Invalid draft operation');
    if (operation === 'write' && clinicalDraftUsersBeingCleared.has(parsedKey.userId)) {
        throw new Error('Draft cleanup in progress');
    }
    return await queueClinicalDraftOperation(key, async () => {
        if (!mixin.methods._isCordovaApp()) {
            if (operation === 'read') {
                const encrypted = window.localStorage.getItem(key);
                return encrypted ? mixin.methods._decrypt(encrypted) : null;
            }
            if (operation === 'delete') window.localStorage.removeItem(key);
            else window.localStorage.setItem(key, mixin.methods._encrypt(value));
            return;
        }
        await waitForClinicalDraftDb();
        return await new Promise((resolve, reject) => {
            const sql = operation === 'read' ? 'SELECT value FROM configuration WHERE id = ?' :
                operation === 'delete' ? 'DELETE FROM configuration WHERE id = ?' :
                    'INSERT OR REPLACE INTO configuration (id, value) VALUES (?, ?)';
            const args = operation === 'write' ? [key, mixin.methods._encrypt(value)] : [key];
            sqlite_db.executeSql(sql, args, result => {
                if (operation !== 'read') return resolve();
                try { resolve(result.rows.length ? mixin.methods._decrypt(result.rows.item(0).value) : null); }
                catch (error) { reject(error); }
            }, reject);
        });
    });
};

const clearClinicalDraftsForUser = async userId => {
    if (typeof userId !== 'string' || !userId || userId.includes(':')) throw new Error('Invalid draft user');
    clinicalDraftUsersBeingCleared.add(userId);
    try {
        // A logout event disables new writes in the active clinical editor. The
        // storage barrier also rejects any write that races with this cleanup.
        const pending = [...clinicalDraftQueues.entries()]
            .filter(([key]) => {
                const parsed = parseClinicalDraftKey(key);
                return parsed && parsed.userId === userId;
            })
            .map(([, operation]) => operation.catch(() => undefined));
        await Promise.all(pending);

        if (!mixin.methods._isCordovaApp()) {
            const keys = [];
            for (let index = 0; index < window.localStorage.length; index += 1) {
                const key = window.localStorage.key(index);
                const parsed = parseClinicalDraftKey(key);
                if (parsed && parsed.userId === userId) keys.push(key);
            }
            keys.forEach(key => window.localStorage.removeItem(key));
            return;
        }

        await waitForClinicalDraftDb();
        const rows = await new Promise((resolve, reject) => {
            sqlite_db.executeSql('SELECT id FROM configuration WHERE id LIKE ?', ['clinical_draft:%'], resolve, reject);
        });
        const keys = [];
        for (let index = 0; index < rows.rows.length; index += 1) {
            const key = rows.rows.item(index).id;
            const parsed = parseClinicalDraftKey(key);
            if (parsed && parsed.userId === userId) keys.push(key);
        }
        await Promise.all(keys.map(key => new Promise((resolve, reject) => {
            sqlite_db.executeSql('DELETE FROM configuration WHERE id = ?', [key], resolve, reject);
        })));
    } finally {
        clinicalDraftUsersBeingCleared.delete(userId);
    }
};

export default {
    clearClinicalDraftsForUser,
    clinicalDraftStorage,
    storageUpsert,
    storageDeleteWithId,
    storageSelect,
    getUserFromStorage,
}
